import { TIMEZONE_HEADER, resolveTimeZone } from '@cch/shared/timezone';

import { API_BASE_URL } from './constants';
import { flushVisitorProductEvents } from './product-events';
import { getVisitorId } from './visitor-id';

export type BodyTypeSlug =
  | 'hourglass'
  | 'rectangle'
  | 'triangle'
  | 'inverted-triangle'
  | 'oval';

export type ColourPaletteSlug = 'spring' | 'summer' | 'autumn' | 'winter';

export type Gender = 'woman' | 'man' | 'other' | 'prefer-not-to-say';

export interface Customer {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  bodyType: BodyTypeSlug | null;
  colourPalette?: ColourPaletteSlug | null;
  gender?: Gender | null;
}

interface MeResponse {
  customer: Customer;
}

export const VISITOR_ID_HEADER = 'X-Visitor-Id';

/**
 * The calls that create a customer session, and therefore the calls that carry
 * the visitor id so the Worker can claim everything that visitor did while
 * signed out.
 *
 * Sent twice on purpose: as a header, and as a field on the JSON body. The
 * header is the general case, the body field survives anything that strips
 * unknown headers between here and the Worker. The Worker treats them as the
 * same value.
 *
 * `/email/request` is included even though the session is only created at
 * `/email/verify`: the request leg is where a signup is attributed, and the
 * two legs can be minutes apart.
 *
 * Exported because a test enumerates the sign-in calls in this file and fails
 * if one of them is missing from this list, so a new sign-in path cannot
 * quietly ship without the link.
 */
export const SIGN_IN_PATHS = [
  '/sso/google',
  '/sso/microsoft',
  '/sso/apple',
  '/email/request',
  '/email/verify',
] as const;

type SignInPath = (typeof SIGN_IN_PATHS)[number];

/** Merge the visitor id into a JSON body, leaving any other body untouched. */
function withVisitorId(body: BodyInit | null | undefined, visitorId: string): BodyInit | null | undefined {
  if (typeof body !== 'string') return body;
  try {
    const parsed: unknown = JSON.parse(body);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return body;
    return JSON.stringify({ ...(parsed as Record<string, unknown>), visitorId });
  } catch {
    return body;
  }
}

/**
 * Every account call, and the only place web attaches the reader's time zone.
 *
 * Web sends none of the client-identification headers mobile sends, which is
 * why `device_model` is null for every web customer. The time zone is the first
 * of them, because it is the one worth having for everyone who registers rather
 * than only for the app.
 *
 * Attached HERE and not in `queryClient.ts`'s `apiRequest`: that helper looks
 * like the chokepoint and is not one, because it sets no credentials at all, so
 * nothing it sends is authenticated. Authenticated web traffic is the three
 * call sites passing `credentials: 'include'`, and this is the one carrying
 * sign-in and `/me`. The other two, `saved-api.ts` and `product-events.ts`, are
 * deliberately left alone: the zone is a property of the customer rather than
 * of a request, so one arriving surface is enough, and three would be three
 * things to keep in step.
 *
 * `resolveTimeZone` is shared with mobile rather than reimplemented, so the one
 * backoffice sanitiser cannot be handed two different value shapes. It returns
 * `undefined` when it has nothing honest to send, and the header is then absent
 * rather than invented.
 *
 * Spread BEFORE `init.headers` so an explicit caller value still wins, matching
 * how `Content-Type` above already behaves.
 */
async function accountFetch(path: string, init?: RequestInit): Promise<Response> {
  const timeZone = resolveTimeZone();
  const visitorId = SIGN_IN_PATHS.includes(path as SignInPath)
    ? getVisitorId()
    : null;

  // Anything the person did before this moment goes out under the visitor id
  // BEFORE the sign-in request, so the Worker's claim sees it. Flushed after
  // the claim it would arrive as an orphan row.
  if (visitorId) flushVisitorProductEvents();

  return fetch(`${API_BASE_URL}/api/account${path}`, {
    ...init,
    credentials: 'include',
    body: visitorId ? withVisitorId(init?.body, visitorId) : init?.body,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(timeZone ? { [TIMEZONE_HEADER]: timeZone } : {}),
      ...(visitorId ? { [VISITOR_ID_HEADER]: visitorId } : {}),
      ...(init?.headers ?? {}),
    },
  });
}

export async function fetchMe(): Promise<Customer | null> {
  const res = await accountFetch('/me');
  if (res.status === 401 || res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load account: ${res.status}`);
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

// Web uses the OAuth authorization-code flow via google.accounts.oauth2.
// initCodeClient. We post the `code` to the Worker, which exchanges it
// server-side for an ID token using the GOOGLE_CUSTOMER_CLIENT_SECRET.
// Native mobile takes a different path (it sends `idToken` directly) and
// uses its own helper.
export async function signInWithGoogle(
  code: string,
  options: { newsletterOptIn?: boolean; signupSource?: string } = {},
): Promise<Customer> {
  const res = await accountFetch('/sso/google', {
    method: 'POST',
    body: JSON.stringify({
      code,
      newsletterOptIn: options.newsletterOptIn,
      signupSource: options.signupSource,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Sign-in failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export async function signInWithMicrosoft(
  idToken: string,
  options: { newsletterOptIn?: boolean; signupSource?: string } = {},
): Promise<Customer> {
  const res = await accountFetch('/sso/microsoft', {
    method: 'POST',
    body: JSON.stringify({
      idToken,
      newsletterOptIn: options.newsletterOptIn,
      signupSource: options.signupSource,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Sign-in failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

// Apple's web popup hands back a signed identity token directly, so — unlike
// Google's web flow — there is no authorization code to exchange server-side
// and no client secret involved. Same wire shape as Microsoft.
//
// Note this deliberately does NOT reuse `createAccountClient().signInWithApple`
// from `@cch/shared`: that client is the mobile bearer-token path (it sends
// `X-Client: mobile` and expects a token in the body), whereas web auth is
// cookie-based via `credentials: 'include'`. Routing web through it would stop
// the session cookie being set.
export async function signInWithApple(
  idToken: string,
  options: { newsletterOptIn?: boolean; signupSource?: string } = {},
): Promise<Customer> {
  const res = await accountFetch('/sso/apple', {
    method: 'POST',
    body: JSON.stringify({
      idToken,
      newsletterOptIn: options.newsletterOptIn,
      signupSource: options.signupSource,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Sign-in failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export async function signOut(): Promise<void> {
  await accountFetch('/logout', { method: 'POST' });
}

// Passwordless email sign-in. The backend always returns 200 OK to avoid
// leaking whether the email exists; failures (network, rate-limit) still
// throw so the UI can show a generic retry message.
// signupSource (when supplied) is persisted to the customer_email_codes row
// at request time and replayed onto the customer record at verify time —
// since /email/verify doesn't accept signupSource on the wire, the only
// chance to attribute an email signup is here.
export async function requestEmailCode(
  email: string,
  options: { newsletterOptIn?: boolean; signupSource?: string } = {},
): Promise<void> {
  const res = await accountFetch('/email/request', {
    method: 'POST',
    body: JSON.stringify({
      email,
      newsletterOptIn: options.newsletterOptIn,
      signupSource: options.signupSource,
    }),
  });
  if (!res.ok) {
    throw new Error(`Email request failed: ${res.status}`);
  }
}

export async function verifyEmailCode(
  email: string,
  code: string,
): Promise<Customer> {
  const res = await accountFetch('/email/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // Surface the backend's "Invalid or expired code" / "Too many attempts"
    // messages so the UI can show specific guidance.
    throw new Error(text || `Verification failed: ${res.status}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export async function updateMe(update: {
  firstName?: string | null;
  lastName?: string | null;
}): Promise<Customer> {
  const res = await accountFetch('/me', {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Update failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export async function submitBodyTypeQuiz(input: {
  source: 'quiz' | 'manual' | 'public_quiz_signup_sync';
  bodyType: BodyTypeSlug;
  answers?: Record<string, unknown>;
}): Promise<Customer> {
  const res = await accountFetch('/me/body-type-quiz', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Body type quiz submission failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export async function updateGender(gender: Gender): Promise<Customer> {
  const res = await accountFetch('/me/gender', {
    method: 'PATCH',
    body: JSON.stringify({ gender }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Gender update failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}

export type QuizType = 'body_type' | 'colour_palette';

// Funnel telemetry: ping the backend when a logged-in customer opens the
// quiz. Failures are non-fatal — the quiz UX must work even if telemetry
// is unreachable.
export async function recordQuizStart(quizType: QuizType): Promise<void> {
  try {
    await accountFetch('/me/quiz-start', {
      method: 'POST',
      body: JSON.stringify({ quizType }),
    });
  } catch {
    // Swallow — telemetry must never block the quiz flow.
  }
}

export async function submitColourPaletteQuiz(input: {
  source: 'quiz' | 'manual' | 'public_quiz_signup_sync';
  colourPalette: ColourPaletteSlug;
  answers?: Record<string, unknown>;
}): Promise<Customer> {
  const res = await accountFetch('/me/colour-palette-quiz', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Colour palette quiz submission failed: ${res.status} ${text}`);
  }
  const data = (await res.json()) as MeResponse;
  return data.customer;
}
